/*
  <reveal-text data-mode="chars|words|sweep" [data-pin]>
  Splits the [data-rt-source] element into small spans and lights them up in
  reading order, driven by how far the block has travelled through the viewport.
*/
(() => {
  if (customElements.get('reveal-text')) return;

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class RevealText extends HTMLElement {
    connectedCallback() {
      this.source = this.querySelector('[data-rt-source]');
      if (!this.source || reduceMotion()) return;

      this.mode = this.dataset.mode || 'chars';
      this.pinned = this.hasAttribute('data-pin');
      this.units = this.split(this.source);
      if (!this.units.length) return;

      this.classList.add('is-split');
      this.litCount = 0;
      this.frame = null;

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.render();
        });
      };

      // Only listen to scroll while the block is near the viewport.
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            window.addEventListener('scroll', this.onScroll, { passive: true });
            window.addEventListener('resize', this.onScroll, { passive: true });
            this.onScroll();
          } else {
            window.removeEventListener('scroll', this.onScroll);
            window.removeEventListener('resize', this.onScroll);
            this.render();
          }
        });
      }, { rootMargin: '10% 0px 10% 0px' });
      this.observer.observe(this);
      this.render();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    /* Wrap every word (and, in character mode, every character) while keeping inline markup. */
    split(root) {
      const units = [];
      const plain = root.textContent.replace(/\s+/g, ' ').trim();
      const walk = (node) => {
        Array.from(node.childNodes).forEach((child) => {
          if (child.nodeType === Node.ELEMENT_NODE) {
            if (child.tagName === 'A') child.setAttribute('tabindex', '-1');
            walk(child);
            return;
          }
          if (child.nodeType !== Node.TEXT_NODE) return;
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((piece) => {
            if (!piece) return;
            if (/^\s+$/.test(piece)) {
              frag.appendChild(document.createTextNode(' '));
              return;
            }
            if (this.mode === 'chars') {
              const word = document.createElement('span');
              word.className = 'rt__word';
              Array.from(piece).forEach((char) => {
                const unit = document.createElement('span');
                unit.className = 'rt__unit';
                unit.textContent = char;
                word.appendChild(unit);
                units.push(unit);
              });
              frag.appendChild(word);
            } else {
              const unit = document.createElement('span');
              unit.className = 'rt__unit rt__word';
              unit.textContent = piece;
              frag.appendChild(unit);
              units.push(unit);
            }
          });
          child.replaceWith(frag);
        });
      };

      // Keep a clean copy for assistive tech; the split version is decorative.
      const copy = document.createElement('span');
      copy.className = 'visually-hidden';
      copy.textContent = plain;
      walk(root);
      const visual = document.createElement('span');
      visual.setAttribute('aria-hidden', 'true');
      while (root.firstChild) visual.appendChild(root.firstChild);
      root.append(copy, visual);
      return units;
    }

    /* 0 → nothing lit, 1 → everything lit. */
    progress() {
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      if (this.pinned) {
        const travel = rect.height - vh;
        if (travel <= 0) return clamp((vh - rect.top) / vh, 0, 1);
        // Leave a short hold at the end so the finished sentence can be read.
        return clamp((-rect.top / travel) * 1.15, 0, 1);
      }
      const textRect = this.source.getBoundingClientRect();
      const start = vh * 0.88;
      const end = vh * 0.42;
      return clamp((start - textRect.top) / (start - end + textRect.height), 0, 1);
    }

    render() {
      const total = this.units.length;
      const exact = this.progress() * total;

      if (this.mode === 'sweep') {
        // Continuous fill: only the words around the moving edge need updating.
        const edge = Math.floor(exact);
        const from = Math.max(0, Math.min(edge, this.litCount) - 1);
        const to = Math.min(total, Math.max(edge, this.litCount) + 2);
        for (let i = from; i < to; i++) {
          const fill = clamp(exact - i, 0, 1) * 100;
          this.units[i].style.setProperty('--fill', `${fill.toFixed(1)}%`);
        }
        this.litCount = edge;
        return;
      }

      const count = Math.round(exact);
      if (count === this.litCount) return;
      if (count > this.litCount) {
        for (let i = this.litCount; i < count; i++) this.units[i].classList.add('is-lit');
      } else {
        for (let i = count; i < this.litCount; i++) this.units[i].classList.remove('is-lit');
      }
      this.litCount = count;
    }
  }

  customElements.define('reveal-text', RevealText);
})();
